import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, take } from 'rxjs';
import { IconModule } from 'src/app/shared/icon/icon.module';
import {
  SettingsApiService,
  type SmtpSettingsData,
  type UpdateSmtpSettingsBody,
} from 'src/app/core/services/settings-api.service';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { validateEmailFormat } from 'src/app/shared/utils/email-format';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';

const DEFAULT_SMTP_PORT = '587';

type SmtpForm = {
  use_custom_smtp: boolean;
  from_email: string;
  host: string;
  port: string;
  secure: boolean;
  auth_user: string;
  auth_password: string;
};

type SmtpFormField = keyof Omit<SmtpForm, 'use_custom_smtp' | 'secure'>;

@Component({
  selector: 'app-smtp-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, IconModule, HasPermissionPipe],
  templateUrl: './smtp-settings.component.html',
})
export class SmtpSettingsComponent implements OnInit {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  private readonly settingsApi = inject(SettingsApiService);
  private readonly snackBar = inject(MatSnackBar);

  smtp: SmtpSettingsData | null = null;
  form: SmtpForm = this.emptyForm();
  loading = false;
  saving = false;
  editMode = false;
  showPassword = false;
  loadError = '';
  saveError = '';
  fieldErrors: Partial<Record<SmtpFormField, string>> = {};

  ngOnInit(): void {
    this.loadSmtpSettings();
  }

  get pageTitle(): string {
    return 'Manage SMTP Settings';
  }

  get pageSubtitle(): string {
    return 'When custom SMTP is enabled and complete, the app sends mail through it. Otherwise email uses the server defaults from environment variables (.env).';
  }

  loadSmtpSettings(): void {
    this.loadError = '';
    this.loading = true;
    this.settingsApi
      .getSmtpSettings()
      .pipe(
        assertSuccessEnvelope(),
        take(1),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (env) => {
          this.smtp = env.data ?? null;
          this.editMode = false;
        },
        error: (err) => {
          this.loadError =
            resolveApiErrorMessage(err) || 'Could not load SMTP settings.';
        },
      });
  }

  startEdit(): void {
    if (!this.smtp) return;
    this.saveError = '';
    this.fieldErrors = {};
    this.form = this.recordToForm(this.smtp);
    this.showPassword = false;
    this.editMode = true;
  }

  cancelEdit(): void {
    this.editMode = false;
    this.saveError = '';
    this.fieldErrors = {};
    this.form = this.smtp ? this.recordToForm(this.smtp) : this.emptyForm();
    this.showPassword = false;
  }

  submit(ev: Event): void {
    ev.preventDefault();
    this.saveError = '';
    this.fieldErrors = {};

    if (!this.validateForm()) {
      return;
    }

    this.saving = true;
    this.settingsApi
      .patchSmtpSettings(this.formToBody())
      .pipe(
        assertSuccessEnvelope(),
        take(1),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (env) => {
          this.smtp = env.data ?? null;
          this.editMode = false;
          this.form.auth_password = '';
          this.snackBar.open(
            env.message || 'SMTP settings saved successfully.',
            'Dismiss',
            { duration: 5000 },
          );
        },
        error: (err) => {
          this.saveError =
            resolveApiErrorMessage(err) || 'Could not save SMTP settings.';
        },
      });
  }

  display(value: string | null | undefined): string {
    const v = (value ?? '').trim();
    return v || '—';
  }

  fieldError(field: SmtpFormField): string {
    return this.fieldErrors[field] ?? '';
  }

  clearFieldError(field: SmtpFormField): void {
    if (!this.fieldErrors[field]) return;
    const { [field]: _removed, ...rest } = this.fieldErrors;
    this.fieldErrors = rest;
  }

  togglePasswordVisible(): void {
    this.showPassword = !this.showPassword;
  }

  private validateForm(): boolean {
    if (!this.form.use_custom_smtp) {
      this.fieldErrors = {};
      return true;
    }

    const errors = this.collectCustomSmtpErrors();
    this.fieldErrors = errors;
    return Object.keys(errors).length === 0;
  }

  private collectCustomSmtpErrors(): Partial<Record<SmtpFormField, string>> {
    const errors: Partial<Record<SmtpFormField, string>> = {};
    const trim = (value: string) => value.trim();

    const fromEmail = trim(this.form.from_email);
    if (!fromEmail) {
      errors.from_email = 'From email is required.';
    } else {
      const emailErr = validateEmailFormat(fromEmail);
      if (emailErr) {
        errors.from_email = emailErr;
      }
    }

    if (!trim(this.form.host)) {
      errors.host = 'SMTP host is required.';
    } else if (trim(this.form.host).length > 255) {
      errors.host = 'SMTP host must be 255 characters or fewer.';
    }

    const portErr = this.validatePort(this.form.port);
    if (portErr) {
      errors.port = portErr;
    }

    if (!trim(this.form.auth_user)) {
      errors.auth_user = 'Login ID is required.';
    } else if (trim(this.form.auth_user).length > 320) {
      errors.auth_user = 'Login ID must be 320 characters or fewer.';
    }

    const needsAuthSecret =
      !this.smtp?.password_configured && !trim(this.form.auth_password);
    if (needsAuthSecret) {
      errors.auth_password = this.smtpAuthSecretRequiredMessage();
    }

    return errors;
  }

  private smtpAuthSecretRequiredMessage(): string {
    return 'Provide the SMTP secret when enabling custom SMTP for the first time.';
  }

  private validatePort(raw: string): string | null {
    const trimmed = raw.trim();
    if (!trimmed) {
      return 'Port is required.';
    }
    if (!/^\d+$/.test(trimmed)) {
      return 'Enter a valid port (1–65535).';
    }
    const port = Number.parseInt(trimmed, 10);
    if (port < 1 || port > 65535) {
      return 'Enter a valid port (1–65535).';
    }
    return null;
  }

  private emptyForm(): SmtpForm {
    return {
      use_custom_smtp: false,
      from_email: '',
      host: '',
      port: DEFAULT_SMTP_PORT,
      secure: false,
      auth_user: '',
      auth_password: '',
    };
  }

  private recordToForm(record: SmtpSettingsData): SmtpForm {
    return {
      use_custom_smtp: record.use_custom_smtp,
      from_email: record.from_email ?? '',
      host: record.host ?? '',
      port: record.port?.trim() ? record.port : DEFAULT_SMTP_PORT,
      secure: record.secure ?? false,
      auth_user: record.auth_user ?? '',
      auth_password: '',
    };
  }

  private formToBody(): UpdateSmtpSettingsBody {
    const body: UpdateSmtpSettingsBody = {
      use_custom_smtp: this.form.use_custom_smtp,
      secure: this.form.secure,
    };

    if (!this.form.use_custom_smtp) {
      return body;
    }

    body.from_email = this.form.from_email.trim();
    body.host = this.form.host.trim();
    body.port = Number.parseInt(this.form.port.trim(), 10);
    body.auth_user = this.form.auth_user.trim();

    const pwd = this.form.auth_password.trim();
    if (pwd) {
      body.auth_password = pwd;
    }

    return body;
  }
}
